import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

export interface PdfStylesheet {
  id: string;
  name: string;
  description?: string;
  css_content: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateStylesheetRequest {
  name: string;
  description?: string;
  css_content: string;
  is_default?: boolean;
}

export interface UpdateStylesheetRequest {
  name?: string;
  description?: string;
  css_content?: string;
  is_default?: boolean;
}

// ==================== QUERIES ====================

/**
 * Fetch all PDF stylesheets
 */
export const useStylesheets = () => {
  return useQuery({
    queryKey: ['stylesheets'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stylesheets`, { headers });
      if (!res.ok) throw new Error('Failed to fetch stylesheets');
      return (await res.json()) as PdfStylesheet[];
    },
  });
};

/**
 * Fetch a specific stylesheet by ID
 */
export const useStylesheet = (id: string | undefined) => {
  return useQuery({
    queryKey: ['stylesheets', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stylesheets/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch stylesheet');
      return (await res.json()) as PdfStylesheet;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new PDF stylesheet
 */
export const useCreateStylesheet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateStylesheetRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stylesheets`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create stylesheet');
      }
      return (await res.json()) as PdfStylesheet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stylesheets'] });
    },
  });
};

/**
 * Update an existing PDF stylesheet
 */
export const useUpdateStylesheet = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateStylesheetRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stylesheets/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update stylesheet');
      }
      return (await res.json()) as PdfStylesheet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stylesheets'] });
      queryClient.invalidateQueries({ queryKey: ['stylesheets', id] });
    },
  });
};

/**
 * Delete a PDF stylesheet
 */
export const useDeleteStylesheet = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stylesheets/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete stylesheet');
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stylesheets'] });
    },
  });
};
